import React, { useEffect, useState, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface Credit {
  id: string;
  companyId: string;
  amountCents: number;
  originalAmountCents: number;
  reason: string;
  appliedInvoiceId: string | null;
  appliedAmountCents: number | null;
  appliedAt: string | null;
  createdAt: string;
}

interface RetroactiveBreakdownEntry {
  monthLabel: string;
  paidCents: number;
  newRateCents: number;
  deltaCents: number;
  paymentIds: string[];
}

interface RetroactiveEstimate {
  totalCents: number;
  breakdown: RetroactiveBreakdownEntry[];
  notes?: string[];
}

const usd = (cents: number) => `$${(cents / 100).toFixed(2)}`;

interface Props {
  companyId: string;
  /**
   * Optional: when provided, renders a "calculate retroactive credit" helper
   * for the given currently-selected discount group ids.
   */
  selectedDiscountGroupIds?: string[];
}

const BillingCreditsPanel: React.FC<Props> = ({ companyId, selectedDiscountGroupIds }) => {
  const [credits, setCredits] = useState<Credit[]>([]);
  const [balanceCents, setBalanceCents] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [showGrantForm, setShowGrantForm] = useState(false);
  const [grantAmount, setGrantAmount] = useState('');
  const [grantReason, setGrantReason] = useState('');

  const [retro, setRetro] = useState<RetroactiveEstimate | null>(null);
  const [retroLoading, setRetroLoading] = useState(false);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('riskninja-token')}`,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/credits`, {
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error('Failed to load credits');
      const data = await r.json();
      setCredits(data.credits || []);
      setBalanceCents(data.balanceCents || 0);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const handleGrant = async () => {
    setError('');
    const dollars = parseFloat(grantAmount);
    if (!Number.isFinite(dollars) || dollars <= 0) { setError('Enter a positive dollar amount'); return; }
    if (!grantReason.trim()) { setError('Reason is required'); return; }
    const cents = Math.round(dollars * 100);
    try {
      const r = await fetch(`${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/credits`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ amountCents: cents, reason: grantReason.trim() }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to grant credit');
      }
      setGrantAmount('');
      setGrantReason('');
      setShowGrantForm(false);
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to grant');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this credit? This cannot be undone.')) return;
    try {
      const r = await fetch(`${API_BASE_URL}/api/system-admin/billing/credits/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to delete');
      }
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to delete');
    }
  };

  const handleCalculateRetro = async () => {
    if (!selectedDiscountGroupIds || selectedDiscountGroupIds.length === 0) {
      setError('Select at least one discount group first');
      return;
    }
    setRetroLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/credits/calculate-retroactive`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ discountGroupIds: selectedDiscountGroupIds }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to calculate');
      }
      const data: RetroactiveEstimate = await r.json();
      setRetro(data);
    } catch (e: any) {
      setError(e.message || 'Failed to calculate');
    } finally {
      setRetroLoading(false);
    }
  };

  const handleGrantRetro = async () => {
    if (!retro || retro.totalCents <= 0) return;
    if (!window.confirm(`Grant ${usd(retro.totalCents)} retroactive credit?`)) return;
    try {
      const r = await fetch(`${API_BASE_URL}/api/system-admin/billing/companies/${companyId}/credits`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          amountCents: retro.totalCents,
          reason: `Retroactive credit for past payments at higher rate (${retro.breakdown.length} month${retro.breakdown.length > 1 ? 's' : ''})`,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to grant');
      }
      setRetro(null);
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to grant');
    }
  };

  return (
    <div className="bg-white dark:bg-dark-surface p-5 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Billing Credits</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Remaining balance: <strong>{usd(balanceCents)}</strong>
          </p>
        </div>
        <button
          onClick={() => setShowGrantForm(!showGrantForm)}
          className="px-3 py-1.5 bg-primary text-white rounded text-sm hover:bg-blue-600"
        >
          {showGrantForm ? 'Cancel' : 'Grant Credit'}
        </button>
      </div>

      {error && <div className="mb-3 px-3 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded text-sm">{error}</div>}

      {showGrantForm && (
        <div className="mb-4 p-4 border border-gray-200 dark:border-gray-700 rounded">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Amount (USD)</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                placeholder="50.00"
                value={grantAmount}
                onChange={(e) => setGrantAmount(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Reason</label>
              <input
                placeholder="e.g. Refund for double-charge"
                value={grantReason}
                onChange={(e) => setGrantReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <button onClick={handleGrant} className="px-3 py-1.5 bg-green-500 text-white rounded text-sm hover:bg-green-600">
            Grant credit
          </button>
        </div>
      )}

      {selectedDiscountGroupIds && selectedDiscountGroupIds.length > 0 && (
        <div className="mb-4 p-4 border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 rounded">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm font-medium text-blue-900 dark:text-blue-200">Apply retroactive credit?</p>
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
                Calculate refund for past months paid at the higher rate before this discount group was assigned.
              </p>
            </div>
            <button
              onClick={handleCalculateRetro}
              disabled={retroLoading}
              className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {retroLoading ? 'Calculating…' : 'Calculate'}
            </button>
          </div>
          {retro && (
            <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-900 dark:text-blue-200">
                Estimated retroactive credit: <strong>{usd(retro.totalCents)}</strong>
              </p>
              {retro.breakdown.length > 0 && (
                <table className="mt-2 w-full text-xs">
                  <thead>
                    <tr className="text-left text-blue-700 dark:text-blue-300">
                      <th className="py-1 pr-3">Month</th>
                      <th className="py-1 pr-3 text-right">Paid</th>
                      <th className="py-1 pr-3 text-right">New rate</th>
                      <th className="py-1 text-right">Delta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {retro.breakdown.map((b) => (
                      <tr key={b.monthLabel} className="border-t border-blue-200 dark:border-blue-800">
                        <td className="py-1 pr-3">{b.monthLabel}</td>
                        <td className="py-1 pr-3 text-right tabular-nums">{usd(b.paidCents)}</td>
                        <td className="py-1 pr-3 text-right tabular-nums">{usd(b.newRateCents)}</td>
                        <td className="py-1 text-right tabular-nums">{usd(b.deltaCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {retro.notes && retro.notes.length > 0 && (
                <ul className="mt-2 text-xs text-blue-700 dark:text-blue-300 list-disc ml-4">
                  {retro.notes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              )}
              {retro.totalCents > 0 && (
                <button
                  onClick={handleGrantRetro}
                  className="mt-3 px-3 py-1.5 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                >
                  Grant {usd(retro.totalCents)} credit
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : credits.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No credits granted yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
              <th className="py-2 pr-3 font-medium">Granted</th>
              <th className="py-2 pr-3 font-medium text-right">Amount</th>
              <th className="py-2 pr-3 font-medium text-right">Remaining</th>
              <th className="py-2 pr-3 font-medium">Reason</th>
              <th className="py-2 font-medium">Status</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {credits.map((c) => (
              <tr key={c.id} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                <td className="py-2 pr-3 text-xs text-gray-500 dark:text-gray-400">{new Date(c.createdAt).toLocaleDateString()}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{usd(c.originalAmountCents)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{usd(c.amountCents)}</td>
                <td className="py-2 pr-3 max-w-xs truncate" title={c.reason}>{c.reason}</td>
                <td className="py-2 text-xs">
                  {c.appliedInvoiceId ? (
                    <span className="text-gray-500 dark:text-gray-400">applied {c.appliedAt ? new Date(c.appliedAt).toLocaleDateString() : ''}</span>
                  ) : c.amountCents === 0 ? (
                    <span className="text-gray-500">depleted</span>
                  ) : (
                    <span className="text-green-600 dark:text-green-400">active</span>
                  )}
                </td>
                <td className="py-2 text-right">
                  {!c.appliedInvoiceId && (
                    <button onClick={() => handleDelete(c.id)} className="text-xs text-red-500 hover:text-red-700">Delete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default BillingCreditsPanel;
